import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Grid, LinearProgress } from "@mui/material";
import { collection, onSnapshot, query, where, orderBy, limit, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { TIER_COLORS } from "../theme";

export function AnalyticsView() {
  const [jobs, setJobs] = useState<any[]>([]);
  const [techs, setTechs] = useState<any[]>([]);

  useEffect(() => {
    const u1 = onSnapshot(query(collection(db, "jobs"), orderBy("createdAt", "desc"), limit(500)),
      snap => setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(collection(db, "technicians"),
      snap => setTechs(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); };
  }, []);

  const completed = jobs.filter(j => j.status === "completed");
  const cancelled = jobs.filter(j => j.status === "cancelled");
  const couldntFix = jobs.filter(j => j.status === "couldnt_fix");
  const totalRevenue = completed.reduce((s, j) => s + (j.totalCharge || 0), 0);
  const totalFees = completed.reduce((s, j) => s + (j.platformFee || 0), 0);
  const completionRate = jobs.length > 0 ? (completed.length / jobs.length * 100).toFixed(1) : "0";
  const avgJobValue = completed.length > 0 ? (totalRevenue / completed.length).toFixed(2) : "0";

  // Last 7 days
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const recentJobs = jobs.filter(j => j.createdAt?.toDate?.() > sevenDaysAgo);
  const recentRevenue = recentJobs.filter(j => j.status === "completed")
    .reduce((s, j) => s + (j.totalCharge || 0), 0);

  // Category breakdown
  const catBreakdown: Record<string, number> = {};
  jobs.forEach(j => {
    if (j.category) catBreakdown[j.category] = (catBreakdown[j.category] || 0) + 1;
  });
  const topCategories = Object.entries(catBreakdown).sort((a, b) => b[1] - a[1]);

  // Tier breakdown
  const tierBreakdown: Record<string, number> = {};
  techs.forEach(t => { if (t.tier) tierBreakdown[t.tier] = (tierBreakdown[t.tier] || 0) + 1; });

  return (
    <Box>
      <Grid container spacing={3} mb={3}>
        {[
          { label: "Total Jobs", value: jobs.length, sub: `${completed.length} completed` },
          { label: "Total Revenue", value: `$${totalRevenue.toFixed(2)}`, sub: "all time" },
          { label: "Platform Fees", value: `$${totalFees.toFixed(2)}`, sub: "your cut" },
          { label: "This Week", value: `$${recentRevenue.toFixed(2)}`, sub: `${recentJobs.length} jobs` },
          { label: "Completion Rate", value: `${completionRate}%`, sub: `${couldntFix.length} couldn't fix` },
          { label: "Avg Job Value", value: `$${avgJobValue}`, sub: "per completed job" },
        ].map(s => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.label}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  {s.label}
                </Typography>
                <Typography variant="h4" fontWeight={800} mt={0.5}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Category breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Jobs by Category</Typography>
              {topCategories.map(([cat, count]) => (
                <Box key={cat} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                      {cat.replace(/_/g, " ")}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>{count}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={jobs.length ? (count / jobs.length) * 100 : 0}
                    sx={{ height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { bgcolor: "#663399" } }}
                  />
                </Box>
              ))}
              {topCategories.length === 0 && (
                <Typography color="text.secondary" variant="body2">No data yet</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Job status breakdown */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>Job Outcomes</Typography>
              {[
                { label: "Completed", count: completed.length, color: "#22c55e" },
                { label: "Couldn't Fix", count: couldntFix.length, color: "#ef4444" },
                { label: "Cancelled", count: cancelled.length, color: "#6b7280" },
                { label: "Active", count: jobs.filter(j => ["pending","accepted","en_route","arrived","in_progress"].includes(j.status)).length, color: "#f59e0b" },
              ].map(s => (
                <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1 }}>{s.label}</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={jobs.length ? (s.count / jobs.length) * 100 : 0}
                    sx={{ flex: 2, height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { bgcolor: s.color } }}
                  />
                  <Typography variant="body2" fontWeight={700} width={30}>{s.count}</Typography>
                </Box>
              ))}

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" fontWeight={700} mb={2}>Tech Tier Distribution</Typography>
                {(["diamond","gold","silver","bronze"] as const).map(tier => (
                  <Box key={tier} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                    <Typography variant="body2" sx={{ color: TIER_COLORS[tier], fontWeight: 700, width: 70, textTransform: "capitalize" }}>
                      {tier}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={techs.length ? ((tierBreakdown[tier] || 0) / techs.length) * 100 : 0}
                      sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { bgcolor: TIER_COLORS[tier] } }}
                    />
                    <Typography variant="body2" fontWeight={700} width={20}>{tierBreakdown[tier] || 0}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
